import { apiClient } from '../infra/http/apiClient'

class ImageService {
  /**
   * Upload an image file
   * @param {File} file - Image file to upload (JPEG, PNG, GIF, WEBP, max 10MB)
   * @param {Object} [api] - API instance
   * @param {string} [customFileName] - Optional custom file name (without extension)
   * @returns {Promise} Promise object containing uploaded image metadata
   */
  static async uploadImage(file, customFileName = null) {
    try {
      // 验证文件类型
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid file type. Only JPEG, PNG, GIF, and WEBP are allowed.')
      }

      // 验证文件大小 (10MB = 10 * 1024 * 1024 bytes)
      const maxSize = 10 * 1024 * 1024
      if (file.size > maxSize) {
        throw new Error('File size exceeds 10MB limit.')
      }

      // 如果需要自定义文件名，创建一个新的 File 对象
      let fileToUpload = file
      if (customFileName) {
        // 获取原始文件的扩展名
        const fileExtension = file.name.split('.').pop() || file.type.split('/')[1] || 'jpg'
        // 创建新的文件名
        const newFileName = `${customFileName}.${fileExtension}`
        // 创建新的 File 对象，保持原始文件的内容和类型
        fileToUpload = new File([file], newFileName, { type: file.type })
      }

      // 创建 FormData
      const formData = new FormData()
      formData.append('file', fileToUpload)

      // 上传文件（需要设置正确的 Content-Type）
      const response = await apiClient.post('/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      return response.data
    } catch (error) {
      console.error('Failed to upload image:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Delete an image
   * @param {string} imageId - Image resource ID
   * @param {Object} [api] - API instance
   * @returns {Promise} Promise object containing deletion result
   */
  static async deleteImage(imageId) {
    try {
      const response = await apiClient.delete(`/image/${imageId}`)
      return response.data
    } catch (error) {
      console.error('Failed to delete image:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get image metadata
   * @param {string} imageId - Image resource ID
   * @param {Object} [api] - API instance
   * @returns {Promise} Promise object containing image metadata
   */
  static async getImage(imageId) {
    try {
      const response = await apiClient.get(`/image/${imageId}`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch image:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get list of images
   * @param {Object} filters - Optional filters
   * @param {string} [filters.ownerId] - Filter by owner user ID (admin/instructor only)
   * @param {number} [filters.page=0] - Page number
   * @param {number} [filters.size=20] - Page size
   * @param {Object} [api] - API instance
   * @returns {Promise} Promise object containing image list
   */
  static async getImages(filters = {}) {
    try {
      const response = await apiClient.get('/images', {
        params: filters,
      })
      return response.data
    } catch (error) {
      console.error('Failed to fetch images:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Delete multiple images
   * @param {string[]} imageIds - Array of image resource IDs to delete (max 50)
   * @returns {Promise} Promise object containing deletion results
   */
  static async deleteMultipleImages(imageIds) {
    try {
      if (!Array.isArray(imageIds) || imageIds.length === 0) {
        throw new Error('Image IDs array is required and cannot be empty.')
      }
      if (imageIds.length > 50) {
        throw new Error('Cannot delete more than 50 images at once.')
      }

      const response = await apiClient.post('/images/delete-multiple', {
        resourceIds: imageIds,
      })
      return response.data
    } catch (error) {
      console.error('Failed to delete multiple images:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Handle API errors and convert to user-friendly messages
   * @private
   */
  static handleError(error) {
    if (error.response) {
      const { status, data } = error.response
      switch (status) {
        case 400:
          return new Error(data.message || 'Invalid request, please check your input.')
        case 401:
          return new Error('Please login to continue.')
        case 403:
          return new Error('You do not have permission to perform this action.')
        case 404:
          return new Error('Image not found.')
        case 413:
          return new Error('File size exceeds the maximum limit of 10MB.')
        case 500:
          return new Error('Server error occurred while processing the image.')
        default:
          return new Error(data.message || 'An unexpected error occurred. Please try again later.')
      }
    }
    // 处理非 HTTP 错误（如文件验证错误）
    if (error.message) {
      return error
    }
    return new Error('Unable to connect to server. Please check your network connection.')
  }
}

export default ImageService
